import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "./Reveal";

const LINKS = [
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Work", href: "#work", testid: "nav-link-work" },
  { label: "About", href: "#about", testid: "nav-link-about" },
  { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export const Navbar = ({ onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    onNavigate(href);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.2, ease: EASE }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "bg-[#050505]/85 backdrop-blur-md border-b border-[#262626]" : "border-b border-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1800px] mx-auto px-6 sm:px-12 lg:px-24 h-16 md:h-20 flex items-center justify-between">
          <button
            onClick={() => go("#home")}
            className="flex items-center gap-3 group"
            data-testid="nav-logo"
          >
            <span className="h-3 w-3 bg-[#FF3B00] transition-transform duration-300 group-hover:rotate-45" />
            <span className="font-oswald font-700 font-bold uppercase tracking-tight text-lg md:text-xl leading-none">
              NorthEdge
            </span>
            <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.3em] text-[#808080] mt-1">
              Contracting
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-10" data-testid="nav-links">
            {LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="font-mono text-xs uppercase tracking-[0.25em] text-[#808080] hover:text-[#FF3B00] transition-colors duration-300"
                data-testid={l.testid}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("#contact")}
              className="group relative overflow-hidden border border-[#FF3B00] px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] text-[#FF3B00]"
              data-testid="nav-cta-button"
            >
              <span className="absolute inset-0 translate-y-full bg-[#FF3B00] transition-transform duration-300 ease-out group-hover:translate-y-0" />
              <span className="relative z-10 flex items-center gap-2 transition-colors duration-300 group-hover:text-[#050505]">
                Start a project
                <ArrowUpRight className="h-3.5 w-3.5" />
              </span>
            </button>
          </nav>

          <button
            className="md:hidden text-[#F2F2F2]"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-menu-toggle"
          >
            <Menu className="h-7 w-7" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-[#050505] flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-[#262626]">
              <span className="font-oswald font-bold uppercase text-lg">NorthEdge</span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
                <X className="h-7 w-7 text-[#FF3B00]" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-6 gap-2">
              {LINKS.map((l, i) => (
                <span key={l.href} className="block overflow-hidden">
                  <motion.button
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1 + i * 0.08, ease: EASE }}
                    onClick={() => go(l.href)}
                    className="font-oswald font-bold uppercase text-5xl tracking-tight text-left hover:text-[#FF3B00] transition-colors duration-300"
                    data-testid={`mobile-${l.testid}`}
                  >
                    {l.label}
                  </motion.button>
                </span>
              ))}
            </nav>
            <p className="px-6 pb-8 font-mono text-[10px] uppercase tracking-[0.3em] text-[#808080]">
              Construction · Commercial · Renovation · Concrete · Snow Removal
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
